import useWebSocket, { ReadyState } from "react-use-websocket";
import { useCallback, useEffect, useState } from "react";

type WebSocketJsonMessage = {
  start?: boolean;
  end?: boolean;
  chunk?: string;
  index?: number;
  metadata?: Record<string, any>;
  questionId?: string;
  sessionid?: string;
};

export type Stream = {
  text: string;
  chunks: string[];
  metadata: Record<string, any>;
};

const yes = () => true;

const useStreamManager = (
  endpoint: string,
  connectionId: string,
  setConnectionId: (connectionId: string) => void,
) => {
  const [streams, setStreams] = useState<Record<string, Stream>>({});
  const [streaming, setSteaming] = useState(false);

  const getStreamById = useCallback(
    (sessionId: string, questionId: string): Stream => {
      if (!streaming) return null;
      const streamId = `${sessionId}-${questionId}`;
      return streams[streamId] || null;
    },
    [streams, streaming],
  );

  // const getEndpoint = useCallback(async () => {
  //   if (ragSettings?.wssEndpoint) return ragSettings.wssEndpoint;
  //   const settings = await fetchRagSettings();
  //   return settings?.wssEndpoint;
  // }, [ragSettings, fetchRagSettings]);

  const consumeConnectionMsg = useCallback(
    (event: MessageEvent) => {
      // grab connectionId passed from server
      try {
        const obj = JSON.parse(event.data);
        if (obj?.connectionId && obj.connectionId !== connectionId) {
          console.log("WS Received ConnectionID", obj);
          //update connectionId in session store
          setConnectionId(obj.connectionId);
        }
      } catch (e) {}
    },
    [connectionId, setConnectionId],
  );

  const { lastJsonMessage, sendMessage, readyState } = useWebSocket(endpoint, {
    share: true,
    onMessage: consumeConnectionMsg,
    //filter: filterMsgs,
    shouldReconnect: yes,
    heartbeat: true,
  });

  // on first connection, get connection id
  useEffect(() => {
    if (readyState === ReadyState.OPEN) {
      // ask for connection id (actual msg doesn't matter)
      // the server will send back the connectionid, which will be handled in the onMessage callback
      console.log("WS Asked for ConnectionID");
      sendMessage("get connectionid");
    }
  }, [readyState, sendMessage]);

  useEffect(() => {
    if (lastJsonMessage) {
      const { start, end, chunk, index, metadata, sessionid, questionId } =
        lastJsonMessage as WebSocketJsonMessage;
      const streamId = `${sessionid}-${questionId}`;
      if (start) {
        setSteaming(true);
        //add stream
        setStreams((prevState) => ({
          ...prevState,
          [streamId]: { text: "", chunks: [], metadata: {} },
        }));
      } else if (end) {
        //remove stream
        setStreams((prevState) => {
          delete prevState[streamId];
          if (Object.keys(prevState).length === 0) setSteaming(false);
          return prevState;
        });
      } else if (questionId) {
        //update stream
        setStreams((prevState) => {
          const currentStream = prevState[streamId];
          // Ignore messages if the stream has ended (removed)
          if (!currentStream) return prevState;

          let text = prevState[streamId]?.text;
          const chunks = prevState[streamId]
            ? [...prevState[streamId].chunks]
            : [];
          if (chunk || chunk === "") {
            // return the answer for which we have all parts in order
            chunks[index] = chunk;
            const missingChunk = chunks.findIndex((c) => c === undefined);
            text = chunks
              .slice(0, missingChunk > -1 ? missingChunk : undefined)
              .join("");
          }

          const { metadata: prevMetadata = {} } = currentStream;
          const { scannedCitations: prevScannedCitations = [] } = prevMetadata;
          const { scannedCitations = [] } = metadata || {};

          const newScannedCitations =
            prevScannedCitations.concat(scannedCitations);

          return {
            ...prevState,
            [streamId]: {
              text,
              chunks,
              metadata: {
                ...prevMetadata,
                ...metadata,
                scannedCitations: newScannedCitations,
              },
            },
          };
        });
      }
    }
  }, [lastJsonMessage]);

  return { streaming, getStreamById };
};

export default useStreamManager;
